import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {SchemaTag} from 'folder-items/FileConstants';
import TagValueViewer from 'metadata/TagValueViewer';
import {getLegacyRequestObjectName, RequestSchema} from 'requests/RequestConstants';
import RequestSampleCell from 'requests/RequestSampleCell';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {BLUE} from 'ui-library/Colors';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {
  PreparedSampleGroup,
  PreparedSampleTag,
  PreparedUISampleGroup,
} from 'workflows/samples/PreparedSampleGroupConstants';
import {SAMPLE_GROUP_SHAPE, SampleGroup, UISampleGroup} from 'workflows/WorkflowConstants';

import {FULFILLMENT_STATUS_NAMES, FulfillmentStatus} from './FulfillmentConstants';
import * as FulfillmentTables from './FulfillmentTables';
import {HYDRATED_FULFILLMENT_SHAPE, HydratedFulfillment} from './HydratedFulfillmentConstants';

interface SampleGroupFieldCellProps {
  field: SchemaFieldConstants.SchemaField;
  sampleGroup: PreparedSampleGroup | PreparedUISampleGroup | SampleGroup | UISampleGroup;
}

function SampleGroupFieldCell(props: SampleGroupFieldCellProps): JSX.Element {
  const tags = props.sampleGroup.tags as Array<SchemaTag | PreparedSampleTag>;
  const tag = tags.find(({name}) => name === props.field.name);
  return (
    <Cell>{tag && <RequestSampleCell samples={tag.targetSamples!} tagSchemaField={props.field} />}</Cell>
  );
}
SampleGroupFieldCell.propTypes = {
  field: SchemaFieldConstants.SCHEMA_FIELD_SHAPE.isRequired,
  sampleGroup: SAMPLE_GROUP_SHAPE.isRequired,
};

export interface FulfillmentStatusTableProps {
  hydratedFulfillments: Array<HydratedFulfillment>;
  fulfillmentIdToNewStatus: {[fulfillmentId: string]: FulfillmentStatus};
  rowStates: Array<{
    id: string;
    selected: boolean;
  }>;
  toggleRowSelected: (rowId: string) => void;
  toggleAllRowsSelected: () => void;
}

export default function FulfillmentStatusTable(props: FulfillmentStatusTableProps): JSX.Element {
  return (
    <Table
      numRows={props.hydratedFulfillments.length}
      onRowClick={(rowIndex: number) => {
        props.toggleRowSelected(props.rowStates[rowIndex].id);
      }}
    >
      <Column
        header={
          <Cell className="simpleTable-checkboxCell">
            <IndeterminateCheckbox
              // eslint-disable-next-line no-restricted-syntax
              items={_.pluck(props.rowStates, 'id')}
              selectedMap={(fulfillmentId) => _.findWhere(props.rowStates, {id: fulfillmentId})!.selected}
              onChange={props.toggleAllRowsSelected}
            />
          </Cell>
        }
        renderCell={(rowIndex: number) => (
          <Cell className="simpleTable-checkboxCell">
            <input
              type="checkbox"
              className={css(styles.checkbox)}
              checked={props.rowStates[rowIndex].selected}
              onChange={() => {
                props.toggleRowSelected(props.rowStates[rowIndex].id);
              }}
            />
          </Cell>
        )}
      />
      {FulfillmentTables.fulfillmentRow(props.hydratedFulfillments, {
        renderSampleGroupField: (field, sampleGroupSchema) => [
          <Column
            key={`sampleGroupField-${field.name}`}
            header={field.displayName}
            renderCell={(rowIndex: number) => {
              const hydratedFulfillment = props.hydratedFulfillments[rowIndex];
              if (
                !hydratedFulfillment.sampleGroup ||
                sampleGroupSchema.id !== hydratedFulfillment.requestSchema.sampleGroupSchema!.id
              ) {
                return null;
              }
              return <SampleGroupFieldCell field={field} sampleGroup={hydratedFulfillment.sampleGroup} />;
            }}
          />,
        ],
        renderRequestID: () => (
          <Column
            key="requestId"
            header={`${getLegacyRequestObjectName()} ID`}
            renderCell={(rowIndex: number) => (
              <Cell>{props.hydratedFulfillments[rowIndex].request.name}</Cell>
            )}
          />
        ),
        renderTask: () => null,
        renderStatus: () => (
          <Column
            key="status"
            header="Status"
            renderCell={(rowIndex: number) => {
              const hydratedFulfillment = props.hydratedFulfillments[rowIndex];
              const newStatus = props.fulfillmentIdToNewStatus[hydratedFulfillment.fulfillment.id];
              const status = hydratedFulfillment.fulfillment.status;
              return <Cell>{FULFILLMENT_STATUS_NAMES[newStatus || status]}</Cell>;
            }}
          />
        ),
        renderRequestField: (field: SchemaFieldConstants.SchemaField, requestSchema: RequestSchema) => (
          <Column
            key={`requestField-${field.name}`}
            header={field.displayName}
            renderCell={(rowIndex: number) => {
              const hydratedFulfillment = props.hydratedFulfillments[rowIndex];
              return (
                <Cell>
                  {requestSchema.id === hydratedFulfillment.requestSchema.id && (
                    <TagValueViewer
                      tag={hydratedFulfillment.request.tags.find(({name}) => name === field.name)!}
                    />
                  )}
                </Cell>
              );
            }}
          />
        ),
        renderRequestTaskField: () => null,
      })}
    </Table>
  );
}
FulfillmentStatusTable.propTypes = {
  hydratedFulfillments: PropTypes.arrayOf(HYDRATED_FULFILLMENT_SHAPE.isRequired).isRequired,
  fulfillmentIdToNewStatus: PropTypes.objectOf(PropTypes.oneOf(Object.values(FulfillmentStatus))).isRequired,

  // rowStateManager
  rowStates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
    }).isRequired
  ).isRequired,
  toggleRowSelected: PropTypes.func.isRequired,
  toggleAllRowsSelected: PropTypes.func.isRequired,
};

export const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});
